import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Card,
  CardBody,
  CardHeader,
  HStack,
  Switch,
  FormControl,
  FormLabel,
  Divider
} from '@chakra-ui/react';
import { theme as mythboundTheme } from '../config/theme';

type SettingsState = {
  reduceMotion: boolean;
  compactSidebar: boolean;
  showTooltips: boolean;
  emailNotifications: boolean;
  discordNotifications: boolean;
  webPushNotifications: boolean;
  autoJoinVoice: boolean;
  pushToTalkDefault: boolean;
  hideOnlineStatus: boolean;
  allowInvites: boolean;
};

const SETTINGS_STORAGE_KEY = 'mythbound_user_settings';

const DEFAULT_SETTINGS: SettingsState = {
  reduceMotion: false,
  compactSidebar: false,
  showTooltips: true,
  emailNotifications: true,
  discordNotifications: true,
  webPushNotifications: false,
  autoJoinVoice: false,
  pushToTalkDefault: false,
  hideOnlineStatus: false,
  allowInvites: true
};

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<SettingsState>;
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      console.warn('[Settings] Failed to load saved preferences:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('[Settings] Failed to persist preferences:', error);
    }
  }, [settings]);

  const handleToggle = useCallback(
    (key: keyof SettingsState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSettings(prev => ({ ...prev, [key]: checked }));
    },
    []
  );

  return (
    <Box>
      <Heading
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.primary.gold}
        mb={2}
      >
        Settings
      </Heading>
      <Text color={mythboundTheme.colors.accent.silver} mb={6}>
        Manage app preferences, notifications, and privacy controls. Profile and screen name settings live on the
        Profile page.
      </Text>

      <VStack spacing={6} align="stretch">
        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Experience
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Reduce motion</FormLabel>
                <Switch isChecked={settings.reduceMotion} onChange={handleToggle('reduceMotion')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Compact sidebar</FormLabel>
                <Switch isChecked={settings.compactSidebar} onChange={handleToggle('compactSidebar')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Show gameplay tooltips</FormLabel>
                <Switch isChecked={settings.showTooltips} onChange={handleToggle('showTooltips')} />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Notifications
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Email alerts</FormLabel>
                <Switch isChecked={settings.emailNotifications} onChange={handleToggle('emailNotifications')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Discord DM updates</FormLabel>
                <Switch isChecked={settings.discordNotifications} onChange={handleToggle('discordNotifications')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Web push notifications</FormLabel>
                <Switch isChecked={settings.webPushNotifications} onChange={handleToggle('webPushNotifications')} />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Voice & Session
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Auto-join voice when a session starts</FormLabel>
                <Switch isChecked={settings.autoJoinVoice} onChange={handleToggle('autoJoinVoice')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Default to push-to-talk</FormLabel>
                <Switch isChecked={settings.pushToTalkDefault} onChange={handleToggle('pushToTalkDefault')} />
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card
          bg={mythboundTheme.colors.secondary.parchment}
          color={mythboundTheme.colors.accent.brown}
          borderRadius="md"
          boxShadow={mythboundTheme.shadows.md}
          borderWidth="1px"
          borderColor={mythboundTheme.colors.primary.gold}
        >
          <CardHeader>
            <Heading size="md" fontFamily={mythboundTheme.typography.fontFamily.heading}>
              Privacy
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Hide online status</FormLabel>
                <Switch isChecked={settings.hideOnlineStatus} onChange={handleToggle('hideOnlineStatus')} />
              </FormControl>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Allow campaign invites</FormLabel>
                <Switch isChecked={settings.allowInvites} onChange={handleToggle('allowInvites')} />
              </FormControl>
              <Divider borderColor="rgba(0,0,0,0.15)" />
              <HStack justify="space-between">
                <Text fontSize="sm" color={mythboundTheme.colors.accent.silver}>
                  Privacy settings sync is local-only for now. Server sync will be added after account settings APIs
                  land.
                </Text>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default Settings;
